import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Canvas, painters, Outline } from 'headbreaker'
import Button from 'components/Button'
import {
	PuzzleContainer,
	PuzzleCanvasWrapper,
	PuzzleCanvas,
	SolvedImage,
	SolvedBanner,
	BannerClose,
	TimerDisplay,
	ButtonRow
} from './styles'
import Confetti from './Confetti'

const BASE_WIDTH = 500;
const BASE_HEIGHT = 600;
const PUZZLE_AREA = 500;
const MAX_VIEWPORT_HEIGHT_RATIO = 0.75;
const PROXIMITY = 20;

const MIN_PIECES = 2;
const MAX_PIECES = 10;
const DEFAULT_PIECES = 5;

const toInRange = (value: unknown, min: number, max: number, fallback: number) => {
	const n = Number(value);
	return Number.isInteger(n) && n >= min && n <= max ? n : fallback;
}

const formatTime = (seconds: number) =>
	`${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

type PuzzleProps = {
	src: string;
	title: string | null | undefined;
	success: string | null | undefined;
	legend: string | null | undefined;
	legendSize: string | null | undefined;
	pieceStyle?: 'regular' | 'alternative' | null;
	columns?: number | string | null;
	rows?: number | string | null;
	shuffleButtonText?: string | null;
	solveButtonText?: string | null;
	timerEnabled?: boolean | null;
}

export default function Puzzle({
	src,
	title = '',
	success = null,
	legend = '',
	legendSize = null,
	pieceStyle = 'regular',
	columns,
	rows,
	shuffleButtonText = null,
	solveButtonText = null,
	timerEnabled = false,
}: PuzzleProps) {
	const puzzleRef = useRef<HTMLDivElement>(null);
	const backgroundRef = useRef<Canvas | null>(null);
	const [solved, setSolved] = useState(false);
	const [ready, setReady] = useState(false);
	const [elapsed, setElapsed] = useState(0);
	const [successClosed, setSuccessClosed] = useState(false);
	const [autoSolved, setAutoSolved] = useState(false);
	const [confettiKey, setConfettiKey] = useState(0);
	const { t } = useTranslation();

	const horizontalPieces = toInRange(columns, MIN_PIECES, MAX_PIECES, DEFAULT_PIECES);
	const verticalPieces = toInRange(rows, MIN_PIECES, MAX_PIECES, DEFAULT_PIECES);

	const fitToScreen = () => {
		const puzzle = puzzleRef.current;
		const background = backgroundRef.current;
		if (!puzzle || !background) return ;
		const availableWidth = puzzle.parentElement?.clientWidth ?? window.innerWidth;
		const availableHeight = window.innerHeight * MAX_VIEWPORT_HEIGHT_RATIO;
		const scale = Math.min(availableWidth / BASE_WIDTH, availableHeight / BASE_HEIGHT);
		background.resize(BASE_WIDTH * scale, BASE_HEIGHT * scale);
		background.scale(scale);
		background.redraw();
	}

	useEffect(() => {
		const puzzle = puzzleRef.current;
		if (!puzzle) return ;
		let newPuzzle = new Image();
		newPuzzle.src = src;
		newPuzzle.onload = () => {
			console.log("loading image...");
			const imageScale = PUZZLE_AREA / Math.max(newPuzzle.naturalWidth, newPuzzle.naturalHeight);
			const pieceSize = {
				x: (newPuzzle.naturalWidth * imageScale) / horizontalPieces,
				y: (newPuzzle.naturalHeight * imageScale) / verticalPieces
			};
			const borderFill = pieceStyle === 'alternative'
				? { x: pieceSize.x / 10, y: pieceSize.y / 10 }
				: { x: 0, y: 0 };
			const background = new Canvas(puzzle.id || 'puzzle', {
				width: BASE_WIDTH,
				height: BASE_HEIGHT,
				pieceSize,
				proximity: Math.min(pieceSize.x, pieceSize.y) / 5,
				borderFill,
				strokeWidth: 2,
				lineSoftness: 0.12,
				image: {
					content: newPuzzle,
					scale: imageScale,
					offset: { x: borderFill.x - pieceSize.x, y: borderFill.y - pieceSize.y }
				},
				painter: new painters.Konva(),
				outline: pieceStyle === 'alternative' ? Outline.Classic : new Outline.Rounded()
			})

			backgroundRef.current = background;

			background.autogenerate({
				horizontalPiecesCount: horizontalPieces,
				verticalPiecesCount: verticalPieces
			})

			background.attachConnectionRequirement((one, other) => {
				const a = one.metadata.targetPosition;
				const b = other.metadata.targetPosition;
				if (!a || !b) return false;
				const dx = (one.centralAnchor.x - a.x) - (other.centralAnchor.x - b.x);
				const dy = (one.centralAnchor.y - a.y) - (other.centralAnchor.y - b.y);
				return Math.abs(dx) <= PROXIMITY && Math.abs(dy) <= PROXIMITY;
			});
			background.puzzle.forceConnectionWhileDragging();
			background.attachSolvedValidator();
			background.onValid(() => setSolved(true));
			background.shuffle(0.7);
			background.draw();
			fitToScreen();
			setReady(true);
		}

		const resizeObserver = new ResizeObserver(fitToScreen);
		resizeObserver.observe(puzzle.parentElement ?? puzzle);
		window.addEventListener('resize', fitToScreen);

		return () => {
			newPuzzle.onload = null;
			resizeObserver.disconnect();
			window.removeEventListener('resize', fitToScreen);
		}
	}, [])

	useEffect(() => {
		if (!timerEnabled || !ready || solved) return ;
		const interval = setInterval(() => setElapsed((seconds) => seconds + 1), 1000);
		return () => clearInterval(interval);
	}, [timerEnabled, ready, solved])

	useEffect(() => {
		if (solved && !autoSolved) setConfettiKey((key) => key + 1);
	}, [solved])

	const solvePuzzle = () => {
		const background = backgroundRef.current;
		if (!background) return ;
		setAutoSolved(true);
		background.solve();
		background.puzzle.validate();
		background.redraw();
	}

	const shufflePuzzle = () => {
		const background = backgroundRef.current;
		if (!background) return ;
		background.shuffle(0.7);
		background.puzzle.validate();
		setSolved(false);
		setSuccessClosed(false);
		setAutoSolved(false);
		setElapsed(0);
		background.redraw();
	}

	return (
		<PuzzleContainer>
			{title && (
				<h2
				  style={{
					margin: '0 0 10px 10px',
					fontSize: '24px',
					fontWeight: '600',
					color: '#333',
					alignSelf: 'center',
				  }}
				>
		  			{title}
				</h2>
	  )}
			{timerEnabled && <TimerDisplay role="timer">{formatTime(elapsed)}</TimerDisplay>}
			<PuzzleCanvasWrapper>
				<PuzzleCanvas ref={puzzleRef} id="puzzle" $hidden={solved} />
				{solved && <SolvedImage src={src} alt={title ?? ''} />}
				{confettiKey > 0 && <Confetti key={confettiKey} />}
				{solved && !successClosed && (
					<SolvedBanner role="status">
						{success ? success : t('puzzle.solved')}
						<BannerClose type="button" aria-label={t('puzzle.close')} onClick={() => setSuccessClosed(true)}>×</BannerClose>
					</SolvedBanner>
				)}
			</PuzzleCanvasWrapper>
			<ButtonRow>
				<Button onClick={solvePuzzle} >{solveButtonText ? solveButtonText : t('puzzle.solve')}</Button>
				<Button onClick={shufflePuzzle} >{shuffleButtonText ? shuffleButtonText : t('puzzle.shuffle')}</Button>
			</ButtonRow>
			{legend && legend.length > 0 && <legend style={{fontSize: legendSize ? legendSize === 'small' ? "13px" : "16px" : '13px'}}>{legend}</legend>}
		</PuzzleContainer>
	)
}
